import React, { Component } from 'react';
import { observer, inject } from 'mobx-react';
import { FormGroup, Button, Intent, TextArea, Icon, Alert, Callout } from "@blueprintjs/core";
import Toast from 'helpers/toaster';
import FormDrawer from 'components/FormDrawer';
import { FormSection, FieldError } from 'components/FormBits';
import ClientAutocomplete from 'components/ClientAutocomplete';
import LocationAutocomplete from 'components/LocationAutocomplete';
import { Api } from 'services/Api';
import { observable } from 'mobx';
import { PHOTO_ACCEPT, PHOTO_MAX_MB, photoProblem, uploadErrorText } from 'helpers/equipmentPhotos';

const isBlank = (value) => value === undefined || value === null || String(value).trim() === '';

// Bring a field with an error into view and put the cursor in it.
const focusField = (id) => {
  setTimeout(() => {
    const field = document.getElementById(id);
    if (!field) return;
    field.scrollIntoView({ block: 'center', behavior: 'smooth' });
    field.focus({ preventScroll: true });
  }, 0);
};

@inject('equipmentStore','store') @observer
class AddEquipmentModal extends Component {
  @observable images = [];
  @observable imageSet = [];
  @observable isDeleteDialogVisible = false;
  @observable isSelectImage;
  @observable errors = {};
  @observable photoError = null;

  componentDidMount() {
    if (this.props.isOpen) {
      this.preselectClient();
    }
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && !prevProps.isOpen) {
      this.errors = {};
      this.preselectClient();
    }
  }

  // Opened with a `client`: select it as if it was picked in the autocomplete.
  // A different client left in the store is cleared first (with its location),
  // as the user would have to do before picking another one.
  preselectClient() {
    let { client } = this.props;
    if (!client) {
      return;
    }
    let { object } = this.props.equipmentStore.persistEquipment;
    if (object.client_id !== client.id) {
      this.onClientClear();
    }
    this.onClientSelect(client);
  }

  onClientSelect(client) {
    let { object } = this.props.equipmentStore.persistEquipment;
    if(object && client){
      object.client_id = client.id
      object.client = client
      this.clearError('client_id');
      this.useOnlyLocation(client);
    }else{
      this.onClientClear();
    }
  }

  // Clears the form and this dialog's picked images. Done after a create and
  // on close (not on open: the work order page presets the client first).
  resetForm() {
    this.props.equipmentStore.clearPersistEquipment();
    this.images = [];
    this.imageSet = [];
  }

  // A client with a single address: choose it, unless one is already chosen.
  useOnlyLocation(client) {
    let { object } = this.props.equipmentStore.persistEquipment;
    if (object.location_id) {
      return;
    }
    Api.post('/locations/search', { search: { term: '*', where: { client_id: client.id }, per_page: 2, page: 1 } })
      .then((response) => {
        const locations = (response.data && response.data.data) || [];
        let { object: current } = this.props.equipmentStore.persistEquipment;
        if (locations.length === 1 && current.client_id === client.id && !current.location_id) {
          current.location_id = locations[0].id;
          current.location = locations[0];
          this.clearError('location_id');
        }
      })
      .catch(() => {});
  }

  handleClosEquipmentDailog() {
    this.resetForm();
    this.props.onClose()
  }

  // Required fields, in the order they appear: key -> [field id, message].
  static REQUIRED = [
    ['client_id', 'equipment-client', 'Choose the client who owns this equipment.'],
    ['location_id', 'equipment-client-location', 'Choose the address where it’s installed.'],
    ['kind', 'equipment-title', 'Say what it is, e.g. Gas furnace.'],
    ['make', 'equipment-make', 'Enter the make.'],
    ['eq_model', 'equipment-model', 'Enter the model.'],
    ['serial_number', 'equipment-serial-number', 'Enter the serial number.'],
    ['eq_location', 'equipment-location', 'Say where it is on site.'],
    ['description', 'equipment-description', 'Add a short description.'],
  ];

  clearError(key) {
    if (this.errors[key]) {
      this.errors = { ...this.errors, [key]: null };
    }
  }

  // Editing asks only what the server requires (and an owner), so older
  // records without an address or description still save.
  static REQUIRED_TO_EDIT = ['client_id', 'make', 'eq_model', 'serial_number'];

  get editing() {
    return Boolean(this.props.equipmentStore.persistEquipment.object.id);
  }

  required(key) {
    return !this.editing || AddEquipmentModal.REQUIRED_TO_EDIT.includes(key);
  }

  // Inline errors under each missing field; focus the first one.
  validate(object) {
    const errors = {};
    let firstId = null;
    AddEquipmentModal.REQUIRED.filter(([key]) => this.required(key)).forEach(([key, id, message]) => {
      if (isBlank(object[key]) && !(key === 'location_id' && errors.client_id)) {
        errors[key] = message;
        firstId = firstId || id;
      }
    });
    this.errors = errors;
    if (firstId) {
      focusField(firstId);
      return false;
    }
    return true;
  }

  createEquipment() {
    const {
      equipmentStore: {
        persistEquipment: {
          object
        }
      }
    } = this.props;
    if (!this.validate(object)) {
      return;
    }
    if (this.editing) {
      this.saveEdits(object);
      return;
    }
    object.images = this.images;

    object.active = true;
    this.props.equipmentStore.createEquipment()
      .then(this.onSuccessAddEquipment.bind(this))
      .catch(this.onFailAddEquipment.bind(this))  
  }

  // Edit mode: send the copy's fields (never photos or parts), stay open on failure.
  saveEdits(object) {
    const name = String(object.kind || object.make || 'Equipment').trim();
    this.props.equipmentStore.saveEquipment(object.id, object)
      .then(() => {
        Toast.show({ message: `${name} saved.`, intent: Intent.SUCCESS });
        this.handleClosEquipmentDailog();
      })
      .catch(this.onFailAddEquipment.bind(this));
  }

  onSuccessAddEquipment(response) {
    this.resetForm();
    this.props.onSuccess(response.data.data)  
    Toast.show({ message: 'Equipment added.', intent: Intent.SUCCESS })
    setTimeout(() => {
      this.props.equipmentStore.loadEquipmentsForWorkReports();
    }, 500);
  }

  onFailAddEquipment(error) {
    const data = error && error.response && error.response.data;
    const reason = data && (data.errors || data.error);
    const fallback = this.editing ? "Couldn't save the equipment. Try again." : "Couldn't add the equipment. Try again.";
    Toast.show({ message: reason ? (Array.isArray(reason) ? reason.join(', ') : String(reason)) : fallback, intent: Intent.DANGER })
  }

  onLocationClear = () => {
    let { object } = this.props.equipmentStore.persistEquipment;
    object.location_id = null;
    object.location = null;
  }

  onClientClear = () => {
    let { object } = this.props.equipmentStore.persistEquipment;
    object.client_id = null;
    object.client = null;
    this.onLocationClear();
  }

  // Check the photo here first, so a wrong type or size says what to do
  // instead of failing on the server.
  onChangeFile(event) {
    const input = event.target;
    const file = input.files && input.files[0];
    input.value = ''; // the same file can be picked again after fixing it
    if (!file) return;
    this.photoError = photoProblem(file);
    if (this.photoError) return;
    this.props.equipmentStore.uploadEquipmentAttachment(file)
      .then(this.onSuccessfullUploadAttachment.bind(this))
      .catch(this.onFailUploadAttachment.bind(this))
  }

  onSuccessfullUploadAttachment(response) {
    response.data.data.attributes.asset.id = response.data.data.attributes.id;
    this.images.push(response.data.data.attributes);
    this.imageSet.push({ src: response.data.data.attributes.asset.url, thumbnail: response.data.data.attributes.asset.thumb.url, id: response.data.data.attributes.id })
    Toast.show({ message: 'Photo added.', intent: Intent.SUCCESS })
  }

  onFailUploadAttachment(error) {
    this.photoError = uploadErrorText(error);
  }

  setImages() {
    this.imageSet = [];
    if (this.images.length > 0) {
      this.images.map((image) => {
        this.imageSet.push({ src: image.asset.url, thumbnail: image.asset.thumb.url, id: image.id })
        return this.imageSet;
      })
    }
  }

  handleSelectCancel() {
    this.isDeleteDialogVisible = false;
  }

  handleSelectConfirm(image) {
    this.isDeleteDialogVisible = true;
    this.isSelectImage = image;
  }

  removeAttachment(attachment) {
    this.isDeleteDialogVisible = false;
    this.images = this.images.filter((image) => image.id !== this.isSelectImage.id);
    this.props.equipmentStore.removeAttachment(this.isSelectImage)
      .then((response) => {
        this.setImages();
      })
  }

  // A text field bound to `object[key]`, with its inline error.
  textField(object, key, { id, label, placeholder, multiline = false }) {
    const error = this.errors[key];
    const required = this.required(key);
    const Input = multiline ? TextArea : 'input';
    return (
      <FormGroup
        className={error ? 'has-error' : undefined}
        label={label}
        labelFor={id}
        labelInfo={required ? '*' : undefined}
      >
        <Input
          id={id}
          type={multiline ? undefined : 'text'}
          className='bp3-input bp3-fill'
          placeholder={placeholder}
          defaultValue={object[key] || ''}
          aria-invalid={!!error}
          aria-describedby={error ? `${id}-error` : undefined}
          onChange={(event) => { object[key] = event.target.value; this.clearError(key); }}
        />
        <FieldError id={`${id}-error`}>{error}</FieldError>
      </FormGroup>
    );
  }

  render() {
    let { object, isLoading } = this.props.equipmentStore.persistEquipment;
    const editing = this.editing;
    const busy = editing ? !!this.props.equipmentStore.isSavingEquipment : !!isLoading;
    const errors = this.errors;
    const client = object.client;
    const doNotService = client && client.attributes && client.attributes.do_not_service;
    const onAlert = client && client.attributes && client.attributes.is_on_alert;
    return(
      <FormDrawer
        size="min(760px, 100vw)"
        title={editing ? 'Edit equipment' : 'New equipment'}
        icon="wrench"
        isOpen={this.props.isOpen}
        onClose={this.handleClosEquipmentDailog.bind(this)}
        busy={busy}
        footer={
          <>
            <Button text="Cancel" onClick={this.handleClosEquipmentDailog.bind(this)} disabled={busy} />
            <Button
              intent={Intent.PRIMARY}
              icon={editing ? 'floppy-disk' : 'add'}
              text={editing ? 'Save changes' : 'Add equipment'}
              loading={busy}
              onClick={this.createEquipment.bind(this)}
            />
          </>
        }
      >
        <FormSection title="Whose and where" hint="The client who owns it and the address it’s installed at.">
          <FormGroup
            className={errors.client_id ? 'has-error' : undefined}
            label="Client"
            labelFor="equipment-client"
            labelInfo="*"
            helperText={errors.client_id ? undefined : 'Search by name, phone number or email.'}
          >
            <ClientAutocomplete
              inputProps={{ id: 'equipment-client', placeholder: 'Start typing a name, phone number or email' }}
              value={object.client}
              onClientSelect={this.onClientSelect.bind(this)}
            />
            <FieldError>{errors.client_id}</FieldError>
          </FormGroup>

          {doNotService && (
            <Callout intent={Intent.DANGER} icon="ban-circle" className="mb-3">
              <strong>Do not service this client.</strong>
              {client.attributes.do_not_service_reason ? ` ${client.attributes.do_not_service_reason}` : ''}
            </Callout>
          )}
          {onAlert && (
            <Callout intent={Intent.WARNING} icon="warning-sign" className="mb-3">
              <strong>Alert on this client.</strong>
              {client.attributes.on_alert_reason ? ` ${client.attributes.on_alert_reason}` : ''}
            </Callout>
          )}

          {
            object.client_id &&
              <FormGroup
                className={errors.location_id ? 'has-error' : undefined}
                label="Address"
                labelFor="equipment-client-location"
                labelInfo={this.required('location_id') ? '*' : undefined}
                helperText={errors.location_id ? undefined : 'One of this client’s locations.'}
              >
                <LocationAutocomplete
                  scope={{ client_id: object.client_id } }
                  inputProps={{ id: 'equipment-client-location', fill: true, placeholder: 'Start typing an address' }}
                  onLocationClear={this.onLocationClear.bind(this)}
                  value={object.location}
                  onLocationSelect={(location) => {
                    if(object && location){
                      object.location_id = location.id;
                      object.location = location
                      this.clearError('location_id');
                    }else{
                      this.onLocationClear();
                    }
                  }}
                />
                <FieldError>{errors.location_id}</FieldError>
              </FormGroup>
          }
        </FormSection>

        <FormSection title="What it is" hint="As written on the unit’s data plate.">
          {this.textField(object, 'kind', { id: 'equipment-title', label: 'Equipment type', placeholder: 'e.g. Gas furnace' })}
          <div className="row">
            <div className="col-6">
              {this.textField(object, 'make', { id: 'equipment-make', label: 'Make', placeholder: 'e.g. Carrier' })}
            </div>
            <div className="col-6">
              {this.textField(object, 'eq_model', { id: 'equipment-model', label: 'Model', placeholder: 'e.g. 59TP6' })}
            </div>
          </div>
          {this.textField(object, 'serial_number', { id: 'equipment-serial-number', label: 'Serial number', placeholder: 'e.g. 2316A12345' })}
          {this.textField(object, 'eq_location', { id: 'equipment-location', label: 'Where on site', placeholder: 'e.g. Basement utility room' })}
          {this.textField(object, 'description', { id: 'equipment-description', label: 'Description', placeholder: 'e.g. 80,000 BTU, 16x25x1 filter, installed 2019', multiline: true })}
        </FormSection>

        {/* Editing: photos are added and deleted on the equipment's page. */}
        {!editing && <FormSection title="Photos" hint="Optional. A photo of the data plate saves a trip later.">
          <div className={`wr-img-upload${this.photoError ? ' has-error' : ''}`}>
            <input
              id="file-upload1"
              type="file"
              multiple={false}
              accept={PHOTO_ACCEPT}
              aria-label="Add a photo"
              aria-describedby="equipment-photo-help"
              onChange={this.onChangeFile.bind(this)}
            />
            <label htmlFor="file-upload1" className="btn-1">
              <span>
                <Icon icon="cloud-upload" style={{marginRight:5}} />
                {this.props.equipmentStore.showUploadAttachmentLoader ? 'Uploading…' : 'Add a photo'}
              </span>
            </label>
            {this.photoError
              ? <FieldError id="equipment-photo-help">{this.photoError}</FieldError>
              : <p className="bp3-form-helper-text" id="equipment-photo-help">JPG, PNG or GIF, up to {PHOTO_MAX_MB} MB.</p>}
          </div>
          {this.imageSet.length > 0 && (
            <div style={{ display: 'flex', flexWrap: 'wrap' }}>
              {
                this.imageSet.map((image, index) =>
                  <div className="wr-img-wrap" key={image.id || index}>
                    <Button icon="trash" className="btn-img-del" aria-label="Remove photo" onClick={() => this.handleSelectConfirm(image)} />
                    <img
                      src={image.thumbnail}
                      alt="Attached to equipment"
                      className="wr-img"
                    />
                  </div>
                )
              }
            </div>
          )}
        </FormSection>}

        <Alert
          cancelButtonText="Cancel"
          confirmButtonText="Delete photo"
          icon="trash"
          intent={Intent.DANGER}
          isOpen={this.isDeleteDialogVisible}
          onCancel={this.handleSelectCancel.bind(this)}
          onConfirm={this.removeAttachment.bind(this)}
        >
          <strong>Delete this photo?</strong>
          <p>It’s removed right away and can’t be brought back.</p>
        </Alert>
      </FormDrawer>

    )
  }

};

export default AddEquipmentModal;
